import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, logger } from "../lib/api";
import { useCart } from "../context/CartContext";

const CheckingView = () => (
  <>
    <div className="mx-auto mb-6 h-14 w-14 rounded-full border-2 border-capra-bronze/30 border-t-capra-bronze animate-spin" />
    <h1 className="font-display text-2xl text-capra-ivory mb-3">Confirming your order…</h1>
    <p className="text-sm text-capra-muted">A moment while we speak with the atelier.</p>
  </>
);

const PaidView = () => (
  <div data-testid="payment-success">
    <img src="/brand/goat-badge.jpg" alt="" className="mx-auto h-16 w-16 rounded-full object-cover border border-capra-bronze/60 mb-6" />
    <p className="overline-label mb-3">Order Confirmed</p>
    <h1 className="font-display text-3xl text-capra-ivory mb-4">Welcome to the House.</h1>
    <p className="text-sm text-capra-muted leading-relaxed mb-8">
      Your pieces are being prepared with care. A confirmation has been recorded for your order.
    </p>
    <Link
      to="/new-collection"
      className="inline-block px-9 py-4 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-capra-amber transition-colors"
    >
      Continue Exploring
    </Link>
  </div>
);

const IssueView = ({ status }) => (
  <div data-testid="payment-issue">
    <h1 className="font-display text-2xl text-capra-ivory mb-4">
      {status === "expired" ? "Session expired." : "We could not confirm your payment."}
    </h1>
    <p className="text-sm text-capra-muted mb-8">If you were charged, contact the maison and we will resolve it promptly.</p>
    <Link
      to="/cart"
      className="inline-block px-9 py-4 border border-capra-bronze/60 text-capra-bronze text-[11px] uppercase tracking-[0.25em] hover:bg-capra-bronze hover:text-capra-night transition-colors"
    >
      Return to Cart
    </Link>
  </div>
);

const usePaymentStatus = (sessionId, onPaid) => {
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 8) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          onPaid();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (err) {
        logger.error("Checkout status poll failed", err);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, onPaid]);

  return status;
};

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const { clearCart } = useCart();
  const status = usePaymentStatus(params.get("session_id"), clearCart);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-32 monogram-veil" data-testid="checkout-success-page">
      <div className="leather-panel p-12 max-w-lg w-full text-center">
        {status === "checking" && <CheckingView />}
        {status === "paid" && <PaidView />}
        {["expired", "error", "timeout"].includes(status) && <IssueView status={status} />}
      </div>
    </div>
  );
}
